/**
 * ScreensWorkConfig — typed replacement for deploy/public/json/screens.json.
 *
 * The SCREENS department work carousel: drag/rotation feel settings plus the
 * four project tiles. Each project carries the floating Pixi particle layers
 * (WorkTilePixi) that hover over its tile and follow it into the case study
 * (`retainInCaseStudy`) — x/y are tile-relative positions, z drives parallax
 * depth, project* fields are the case-study positions.
 *
 * Values are a field-for-field copy of the retired JSON (deep-equal-proven).
 * Array order is preserved from the JSON; note WorkController.setJSON sorts
 * projects by `order` (and drops `active: false` ones) before use.
 */
import { TitleLineType, type ProjectConfig } from 'app/model/ProjectConfig';
import { HeaderType } from 'app/views/project/project.types';
import type { WorkConfig } from 'app/model/WorkConfig';

/**
 * March for Giants — elephant-march campaign. Video header, double title line.
 * City scene particles: taxis, signs, buildings, balloons and the elephants.
 */
export const marchForGiants: ProjectConfig = {
	order: 1,
	slug: 'march-for-giants',
	image: 'm4g.jpg',
	headerType: HeaderType.VIDEO,
	tint: '0xe169ff',
	titleLineType: TitleLineType.DOUBLE,
	particles: [
		// m4g-taxi — hidden in case study
		{
			texture: 'm4g-taxi.png',
			x: 0.2,
			y: 0.97,
			z: 3,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.97,
			caseStudyHide: true
		},
		// m4g-sign — hidden in case study
		{
			texture: 'm4g-sign.png',
			x: 0.13,
			y: 0.5,
			z: 5,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.56,
			mobileProjectY: 0.69,
			mobileProjectX: 0.05,
			caseStudyHide: true
		},
		// m4g-taxis — hidden in case study
		{
			texture: 'm4g-taxis.png',
			x: 0.84,
			y: 0.95,
			z: 3.5,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.93,
			caseStudyHide: true
		},
		// m4g-b1
		{
			texture: 'm4g-b1.png',
			x: 0.2,
			y: 1.01,
			z: 2,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b2
		{
			texture: 'm4g-b2.png',
			x: 0.0,
			y: 0.8,
			z: 3,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b3
		{
			texture: 'm4g-b3.png',
			x: -0.04,
			y: 0.2,
			z: 9,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b4
		{
			texture: 'm4g-b4.png',
			x: 0.18,
			y: -0.02,
			z: 4.5,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b5
		{
			texture: 'm4g-b5.png',
			x: 0.48,
			y: -0.03,
			z: 2,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b6
		{
			texture: 'm4g-b6.png',
			x: 0.75,
			y: -0.04,
			z: 5,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b7
		{
			texture: 'm4g-b7.png',
			x: 1.04,
			y: 0.1,
			z: 6,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b8
		{
			texture: 'm4g-b8.png',
			x: 0.95,
			y: 0.85,
			z: 9,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true,
			projectX: 1.05
		}
	]
};

/**
 * Toy Town — playful building-blocks world. Image header, dark top UI theme,
 * tile anchored at 0.9.
 */
export const toyTown: ProjectConfig = {
	order: 2,
	slug: 'toy-town',
	image: 'toy-town.jpg',
	anchor: 0.9,
	headerType: HeaderType.IMAGE,
	topUiTheme: 'dark',
	tint: '0x06c8f8',
	titleLineType: TitleLineType.SINGLE,
	particles: [
		// tt-balloon-tiny
		{
			texture: 'tt-balloon-tiny.png',
			x: 0.5,
			mobileX: 0.43,
			y: 0.3,
			mobileY: 0.2,
			z: 2,
			depth: 1,
			scale: 0.8,
			projectAnimationOffset: 0.1,
			retainInCaseStudy: true,
			mobileProjectX: 0.3
		},
		// tt-large-cloud
		{
			texture: 'tt-large-cloud.png',
			x: 0.4,
			mobileX: 0.3,
			y: 0.06,
			mobileY: 0.04,
			z: 4,
			scale: 0.8,
			mobileScale: 0.5,
			projectX: 0.4,
			projectY: 0.1,
			mobileProjectX: 0.28,
			mobileProjectY: 0.03,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyEase: 4,
			projectAnimationOffset: 0.2
		},
		// tt-large-cloud
		{
			texture: 'tt-large-cloud.png',
			x: 0.35,
			y: 0.3,
			z: 2.5,
			scale: 0.3,
			projectScale: 0.4,
			projectY: 0.4,
			projectAnimationOffset: 0.2,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyEase: 4,
			alpha: 0.95
		},
		// tt-med-cloud
		{
			texture: 'tt-med-cloud.png',
			x: 0.92,
			mobileX: 0.95,
			y: 0.4,
			mobileY: 0.5,
			z: 3,
			projectX: 0.9,
			scale: 0.8,
			mobileScale: 0.5,
			projectY: 0.5,
			depth: 1,
			retainInCaseStudy: true,
			caseStudyEase: 3,
			projectAnimationOffset: 0.2
		},
		// tt-small-cloud
		{
			texture: 'tt-small-cloud.png',
			x: 0.05,
			y: 0.43,
			z: 2,
			scale: 0.8,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.2
		},
		// tt-masked-cloud
		{
			texture: 'tt-masked-cloud.png',
			x: 0.7,
			y: 0.05,
			z: 3,
			scale: 0.8,
			depth: -2,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.2
		},
		// tt-balloon
		{
			texture: 'tt-balloon.png',
			x: 0.05,
			y: 0.4,
			z: 4,
			depth: 1,
			scale: 0.8,
			projectX: 0.13,
			projectAnimationOffset: 0.2,
			projectY: 0.3,
			mobileProjectX: 0.1,
			mobileProjectY: 0.4,
			retainInCaseStudy: true
		},
		// tt-basket
		{
			texture: 'tt-basket.png',
			x: 0.7,
			y: 0.5,
			z: 6,
			scale: 0.8,
			projectX: 0.7,
			projectY: 0.5,
			projectMobileScale: 1.3,
			projectAnimationOffset: 0.1,
			mobileProjectY: 0.45,
			mobileProjectX: 0.7,
			depth: -2,
			retainInCaseStudy: true
		}
	]
};

/**
 * Total Darkness — science-adventure experience (order 4, shown after Market
 * Masters). Image header. Torch/glow particles over a dark tile.
 */
export const totalDarkness: ProjectConfig = {
	order: 4,
	slug: 'total-darkness',
	image: 'total-darkness.jpg',
	headerType: HeaderType.IMAGE,
	anchor: 0.7,
	tint: '0x06c8f8',
	particles: [
		// moon — Moon
		{
			note: 'Moon',
			texture: 'moon.png',
			scale: 0.8,
			x: 0.6,
			y: 0.05,
			z: 1.2,
			depth: -2,
			retainInCaseStudy: true,
			projectY: 0.001
		},
		// td-character-2 — Character Left
		{
			note: 'Character Left',
			texture: 'td-character-2.png',
			scale: 0.7,
			mobileScale: 0.6,
			x: 0.6,
			mobileX: 0.58,
			y: 0.63,
			mobileY: 0.68,
			z: 2,
			depth: -2,
			retainInCaseStudy: true,
			projectX: 0.54,
			mobileProjectX: 0.4,
			projectMobileScale: 1.2,
			projectAnimationOffset: 0.08
		},
		// td-character-3 — Character Right
		{
			note: 'Character Right',
			texture: 'td-character-3.png',
			scale: 0.8,
			mobileScale: 0.68,
			x: 0.86,
			mobileX: 0.89,
			y: 0.6,
			mobileY: 0.69,
			z: 2,
			depth: -2,
			retainInCaseStudy: true,
			projectX: 0.84,
			mobileProjectY: 0.66,
			projectMobileScale: 1.2,
			projectAnimationOffset: 0.08
		},
		// td-character-1 — Character Middle
		{
			note: 'Character Middle',
			texture: 'td-character-1.png',
			scale: 0.7,
			mobileScale: 0.6,
			x: 0.72,
			mobileX: 0.73,
			y: 0.6,
			mobileY: 0.68,
			z: 3,
			depth: -2,
			retainInCaseStudy: true,
			projectX: 0.68,
			mobileProjectX: 0.62,
			projectY: 0.65,
			projectMobileScale: 1.3,
			projectAnimationOffset: 0.15
		},
		// td-bush-mg — Left Bush
		{
			note: 'Left Bush',
			texture: 'td-bush-mg.png',
			scale: 0.8,
			x: 0.1,
			y: 0.9,
			z: 1.5,
			depth: -2,
			retainInCaseStudy: true,
			projectAnimationOffset: 0
		},
		// td-bush-fg — Right Bush
		{
			note: 'Right Bush',
			texture: 'td-bush-fg.png',
			scale: 0.8,
			x: 0.86,
			y: 0.97,
			mobileY: 0.985,
			z: 4,
			depth: -2,
			retainInCaseStudy: true,
			projectAnimationOffset: 0,
			projectY: 0.95,
			mobileProjectY: 0.96,
			projectMobileScale: 1.2
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.35,
			x: -0.05,
			y: 0.5,
			z: 4,
			depth: 1,
			retainInCaseStudy: true,
			projectScale: 0.35
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.3,
			x: 0,
			y: 0.4,
			z: 2,
			depth: 1,
			retainInCaseStudy: true,
			projectScale: 0.3,
			projectAnimationOffset: 1.4
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.28,
			x: 0.06,
			y: 0.45,
			z: 3,
			depth: 1,
			retainInCaseStudy: true,
			projectScale: 0.28
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.2,
			x: 0.25,
			y: -0.03,
			z: 4,
			depth: 1,
			retainInCaseStudy: true,
			projectY: 0.04,
			projectScale: 0.25,
			projectAnimationOffset: 1.8
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.6,
			x: 0.35,
			y: -0.02,
			z: 5.5,
			depth: 1,
			retainInCaseStudy: true,
			projectY: 0.02,
			projectScale: 0.6
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.8,
			x: 0.5,
			y: -0.05,
			z: 7,
			depth: 1,
			retainInCaseStudy: true,
			projectY: 0.06
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.2,
			x: 0.9,
			y: -0.03,
			z: 2,
			depth: 1,
			retainInCaseStudy: true
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.7,
			x: 1.03,
			y: 0.45,
			z: 6,
			depth: 1,
			retainInCaseStudy: true
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.45,
			x: 1.04,
			y: 0.03,
			z: 4.5,
			depth: 1,
			retainInCaseStudy: true,
			projectX: 0.97,
			projectScale: 0.5,
			projectAnimationOffset: 1
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.25,
			x: 1.075,
			y: 0.25,
			z: 2,
			depth: 1,
			retainInCaseStudy: true,
			projectX: 0.99,
			projectScale: 0.3,
			projectAnimationOffset: 1.6
		},
		// star — Star
		{
			note: 'Star',
			texture: 'star.png',
			scale: 0.15,
			x: 1.08,
			y: 0.35,
			z: 1.2,
			depth: 1,
			retainInCaseStudy: true
		}
	]
};

/**
 * Market Masters — stock-market trading game (order 3). Line-graph, price tag,
 * globe and floating currency-symbol particles.
 */
export const marketMasters: ProjectConfig = {
	order: 3,
	slug: 'market-masters',
	image: 'market-masters.jpg',
	tint: '0x990000',
	particles: [
		// mm-line — id 'Line graph'
		{
			id: 'Line graph',
			texture: 'mm-line.png',
			x: 0.4,
			y: 0.5,
			z: 3,
			scale: 0.75,
			speed: 0,
			depth: -2,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.25
		},
		// mm-price-xxx — id 'Line price'
		{
			id: 'Line price',
			texture: 'mm-price-xxx.png',
			x: 0.703,
			y: 0.272,
			z: 2,
			depth: -2,
			autoScale: true,
			retainInCaseStudy: true,
			projectScale: 4,
			projectX: 0.706,
			projectY: 0.25
		},
		// mm-globe
		{
			texture: 'mm-globe.png',
			x: 0.25,
			y: 0.65,
			z: 4.5,
			scale: 0.9,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.2
		},
		// mm-blur-line
		{
			texture: 'mm-blur-line.png',
			x: 0.1,
			y: 0.5,
			z: 7,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.2
		},
		// mm-c1
		{
			texture: 'mm-c1.png',
			x: 0.8,
			y: -0.01,
			z: 5,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.1
		},
		// mm-c2
		{
			texture: 'mm-c2.png',
			x: 0.83,
			y: 0.5,
			z: 3.5,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.7
		},
		// mm-c3
		{
			texture: 'mm-c3.png',
			x: 0.6,
			y: 0.01,
			z: 7,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 1
		},
		// mm-c4
		{
			texture: 'mm-c4.png',
			x: 0.78,
			y: 0.75,
			z: 4,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.6
		},
		// mm-c5
		{
			texture: 'mm-c5.png',
			x: 0.86,
			y: 0.2,
			z: 2.5,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.8
		},
		// mm-c6
		{
			texture: 'mm-c6.png',
			x: 0.02,
			y: 0.8,
			z: 6,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.7
		},
		// mm-c7
		{
			texture: 'mm-c7.png',
			x: 0.75,
			y: 0.3,
			z: 7,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true
		},
		// mm-c8
		{
			texture: 'mm-c8.png',
			x: 0.6,
			y: 0.55,
			z: 5,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 1.6
		},
		// mm-c9
		{
			texture: 'mm-c9.png',
			x: 0.4,
			y: 0.33,
			z: 2,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 2.3
		},
		// mm-c10
		{
			texture: 'mm-c10.png',
			x: 0.3,
			y: 0.27,
			z: 2,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 1
		},
		// mm-c11
		{
			texture: 'mm-c11.png',
			x: 0.49,
			y: 0.65,
			z: 2,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.6
		},
		// mm-c12
		{
			texture: 'mm-c12.png',
			x: 0.4,
			y: 0.2,
			z: 3,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 2.1
		},
		// mm-c13
		{
			texture: 'mm-c13.png',
			x: 0.25,
			y: 0.32,
			z: 3,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.8
		},
		// mm-c14
		{
			texture: 'mm-c14.png',
			x: 0.86,
			y: 0.8,
			z: 5,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true
		},
		// mm-c15
		{
			texture: 'mm-c15.png',
			x: 0.99,
			y: 0.4,
			z: 7,
			scale: 0.75,
			speed: 0,
			depth: 1,
			retainInCaseStudy: true,
			projectAnimationOffset: 0.7
		}
	]
};

/**
 * The assembled department config, in the ORIGINAL JSON array order.
 * WorkController.setJSON filters (`active !== false`) and sorts by `order`
 * before use, but the source order is preserved here for parity.
 */
export const ScreensWorkConfig: WorkConfig = {
	dragEasePower: 2, //Ease power while dragging
	easePower: 18, //Ease power while settling
	velocityFactor: 2.5, //Multiplies timeline movement into the drag/slide rotation effect
	velocityFriction: 0.9, //Per-frame falloff pulling the rotation back to 0
	rotationFactor: 45, //Base level of rotation (degrees)
	maxRotation: 35, //Max rotation allowed either way (can exceed base)
	dragToMoveRatio: 1, //Drag distance -> panel movement ratio (1 = 1:1)
	projects: [marchForGiants, toyTown, totalDarkness, marketMasters]
};
